import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, ListItemText, Menu, MenuItem } from "@mui/material";
import { MouseEvent, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { DealerAccountContext } from "../../../../../context/DealerAccountContext";

export type D2DStatus =
  | "NEW"
  | "ACCEPTED"
  | "REJECTED"
  | "EXPIRED"
  | "SHIPPED"
  | "COMPLETED"
  | "CREDITED";

export interface D2DOrderRow {
  id: string;
  orderNumber: string;
  dealerReference: string;
  partNumber: string;
  requestedQuantity: number;
  acceptedQuantity: number;
  dateRequested: string;
  timeToRespond?: string;
  status: D2DStatus;
  lastChangedBy: string;
  rejectionType?: string;
  rejectionTrigger?: string;
  carrierName?: string;
  trackingNumber?: string;
  weight?: string;
  buyerAccountId?: string;
}

interface Props {
  request: D2DOrderRow;
  onViewOrderSummary: (request: D2DOrderRow) => void;
}

// Single source of truth for which row actions are available per D2D status.
export const hasInvoiceAction = (status: D2DStatus): boolean =>
  status === "COMPLETED" || status === "CREDITED";

export const hasOperationalRowAction = (status: D2DStatus): boolean =>
  status === "NEW" || status === "ACCEPTED";

// Summary is available whenever the row has no operational action (Accept/Reject/Mark as shipped).
export const canViewOrderSummary = (row: D2DOrderRow): boolean =>
  !hasOperationalRowAction(row.status);

export function D2DActionMenu({ request, onViewOrderSummary }: Props) {
  const { t } = useTranslation(["d2d"]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { dealerAccount } = useContext(DealerAccountContext);
  const navigate = useNavigate();

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewSummary = () => {
    onViewOrderSummary(request);
    handleClose();
  };

  const handleViewInvoices = () => {
    const query = dealerAccount?.id ? `?accountId=${dealerAccount.id}` : "";
    const filters = new URLSearchParams({
      dealerReference: request.orderNumber,
      invoiceType: "Credit Memo",
    });
    navigate(`/invoices${query}#${filters.toString()}`);
    handleClose();
  };

  // const handleAcceptClick = () => {
  //   // onAccept(request);
  //   handleClose();
  // };
  //
  // const handleRejectClick = () => {
  //   // onReject(request);
  //   handleClose();
  // };
  //
  // const handleShipClick = () => {
  //   // onShip(request);
  //   handleClose();
  // };

  const menuButtonLabel =
    (t("d2d:d2dRequests.actionMenu.open", "More actions") as string) ||
    "More actions";

  const showSummary = canViewOrderSummary(request);
  const showInvoice = hasInvoiceAction(request.status);

  // Hide the whole menu when it would contain no items.
  if (!showSummary && !showInvoice) {
    return null;
  }

  return (
    <>
      <IconButton
        aria-label={menuButtonLabel}
        onClick={handleMenuOpen}
        size="small"
        data-testid={`d2d-action-menu-${request.id}`}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {showSummary && (
          <MenuItem onClick={handleViewSummary}>
            <ListItemText
              primary={t(
                "d2d:d2dRequests.actionMenu.viewOrderSummary",
                "View order summary"
              )}
            />
          </MenuItem>
        )}
        {showInvoice && (
          <MenuItem onClick={handleViewInvoices}>
            <ListItemText
              primary={t(
                "d2d:d2dRequests.actionMenu.viewInvoice",
                "View Invoice"
              )}
            />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
